import { useCallback, useEffect, useReducer } from 'react';
import toast from 'react-hot-toast';
import { fetchBusinessApi, postBusiness, putBusiness } from '~/api';
import { DEFAULT_BUSINESS } from '~/constants/defaultStates';
import { FILL_REQUIRED_FIELDS } from '~/constants/errors';
import { IBusiness } from '~/types/business';
import { ApiResponseEnum, apiResponseHandler } from '~/utils/api';
import { hasErrorFields, toFloat } from '~/utils/utils';
import { useSelector, useDispatch } from '~/store';
import { changeBusiness, addCompany } from '~/actions';
import { selectUser } from '~/store/slices/formSlice/selectors';
import { RootState } from '~/types';
import { setCompanies } from '~/store/slices/user';

interface State {
  business: IBusiness;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
}

const initialState: State = {
  business: { ...DEFAULT_BUSINESS },
  isLoading: false,
  isSaving: false,
  error: null,
};

type Action =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; payload: IBusiness }
  | { type: 'FETCH_FAILURE'; error: string }
  | { type: 'UPDATE_BUSINESS'; payload: Partial<IBusiness> }
  | { type: 'SAVE_START' }
  | { type: 'SAVE_SUCCESS' }
  | { type: 'SAVE_FAILURE'; error: string }
  | { type: 'RESET' };

const businessReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, isLoading: true, error: null };

    case 'FETCH_SUCCESS':
      return { ...state, isLoading: false, business: action.payload };

    case 'FETCH_FAILURE':
      return { ...state, isLoading: false, error: action.error };

    case 'UPDATE_BUSINESS':
      return { ...state, business: { ...state.business, ...action.payload } };

    case 'SAVE_START':
      return { ...state, isSaving: true, error: null };

    case 'SAVE_SUCCESS':
      return { ...state, isSaving: false };

    case 'SAVE_FAILURE':
      return { ...state, isSaving: false, error: action.error };

    case 'RESET':
      return initialState;

    default:
      return state;
  }
};

interface IUseAddBusinessProps {
  businessUuid?: string | null;
  isAccountSetup?: boolean;
}

export const useAddBusiness = ({
  businessUuid = null,
  isAccountSetup = false,
}: IUseAddBusinessProps) => {
  const [state, dispatch] = useReducer(businessReducer, initialState);
  const reduxDispatch = useDispatch();
  const user = useSelector(selectUser);
  const companies = useSelector((state: RootState) => state.user.companies);

  // Fetch existing client data if in edit mode or fill default values
  const fetchBusiness = useCallback(async () => {
    if (!businessUuid) {
      dispatch({
        type: 'UPDATE_BUSINESS',
        payload: { first_name: user.first_name, last_name: user.last_name },
      });
      return;
    }
    dispatch({ type: 'FETCH_START' });

    const promise = await fetchBusinessApi(businessUuid);
    const response = await apiResponseHandler<IBusiness>(promise);

    if (response.type === ApiResponseEnum.Success) {
      dispatch({ type: 'FETCH_SUCCESS', payload: response.data });
    } else {
      dispatch({ type: 'FETCH_FAILURE', error: response.error });
    }
  }, [businessUuid]);

  useEffect(() => {
    fetchBusiness();
  }, [fetchBusiness]);

  // const navigate = useNavigate();

  // Update form state
  const onChange = (updates: Partial<IBusiness>) => {
    dispatch({ type: 'UPDATE_BUSINESS', payload: updates });
  };

  const getSerializedData = () => {
    const serialized = { ...state.business };
    serialized.tax = toFloat(serialized.tax);
    serialized.discount = toFloat(serialized.discount);
    serialized.shipping = toFloat(serialized.shipping);
    return serialized;
  };

  // Save client (POST for add, PUT for edit)
  const saveBusiness = async () => {
    if (hasErrorFields()) {
      toast.error(FILL_REQUIRED_FIELDS);
      return;
    }

    dispatch({ type: 'SAVE_START' });

    let promise: Promise<any>;
    const data = getSerializedData();
    if (businessUuid) {
      promise = await putBusiness(businessUuid, data);
    } else {
      promise = await postBusiness(data);
    }

    const response = await apiResponseHandler<IBusiness>(promise);

    if (response.type === ApiResponseEnum.Success) {
      dispatch({ type: 'SAVE_SUCCESS' });
      toast.success('Saved business details');
      reduxDispatch(changeBusiness({ ...response.data }));

      // If this is a new business (not editing), add it to the companies list
      if (!businessUuid) {
        reduxDispatch(addCompany({ ...response.data }));
      }

      // If this is account setup flow, call google conversion api
      if (!businessUuid && isAccountSetup) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'conversion',
          send_to: 'AW-17676899135/vYn5CK6Gq7cbEL-mgO1B',
        });
      }

      // If this is edit mode, update company list in redux as well
      if (businessUuid) {
        // Update the specific company in the companies array
        const updatedCompanies = companies.map(company =>
          company.business_uuid === businessUuid ? { ...company, ...response.data } : company,
        );
        reduxDispatch(setCompanies(updatedCompanies));
      }
    } else {
      dispatch({ type: 'SAVE_FAILURE', error: response.error });
      toast.error(response.error ?? 'Error occurred in saving business details');
    }
  };

  return {
    ...state,
    onChange,
    saveBusiness,
    reset: () => dispatch({ type: 'RESET' }),
  };
};
